import { ReactNode, createContext, useContext } from 'react';
import { defaultLocaleFormatters } from '../constants/locale';
import { useAuth } from './AuthContext';
import { 
    formatWithAbbreviation,
    formatWithTooltip, 
    formatQuantity,
    formatPrice
} from '../utils/advancedFormatters';

interface FormatterContextType {
     // Classic formatters
    formatNumber: (value: number) => string;
    formatCrypto: (value: number) => string;
    formatDate: (value: number) => string;
    formatCurrency: (value: number, currencyOverride?: string) => string;
    formatPercentage: (value: number) => string;
    formatDateTwoLines: (
        value: number,
        overrideCountryCode?: string
    ) => {
        date: string;
        time: string;
    };
    formatDateShort: (date: Date | number, overrideCountryCode?: string) => string;
    formatDateLong: (date: Date | number, overrideCountryCode?: string) => string;
    formatMonthYear: (date: Date | number, overrideCountryCode?: string) => string;
    formatTransactionDate: (timestamp: number) => { date: string; time: string };
    // Advanced formatters
    formatSmartNumber: (value: number) => string;
    formatSmartQuantity: (value: number) => string;
    formatSmartPrice: (value: number) => string;
    formatSmartCurrency: (value: number) => string;
    formatWithTooltip: (value: number) => { display: string; tooltip: string };
}

const FormatterContext = createContext<FormatterContextType>({} as FormatterContextType);

export const useFormatter = () => {
    const context = useContext(FormatterContext);
    if (!context) {
        throw new Error('useFormatter must be used within an FormatterProvider');
    }
    return context;
};

export const FormatterProvider = ({ children }: { children: ReactNode }) => {
    const { currentCtUser } = useAuth();

    const currency = currentCtUser?.currency || 'USD';
    const countryCode = currentCtUser?.locale || defaultLocaleFormatters;

    const formatCurrency = (value: number, currencyOverride?: string): string => {
        return new Intl.NumberFormat(countryCode, {
            style: 'currency',
            currency: currencyOverride || currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    };


    const formatPercentage = (value: number): string => {
        return new Intl.NumberFormat(countryCode, {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value / 100);
    };

    const formatNumber = (value: number): string => {
        return new Intl.NumberFormat(countryCode, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(value);
    };

    const formatCrypto = (value: number): string => {
        return new Intl.NumberFormat(countryCode, {
            minimumFractionDigits: 6,
            maximumFractionDigits: 8,
            useGrouping: true,
        }).format(value);
    };

    const formatDate = (timestamp: number): string => {
        const date = new Date(timestamp * 1000);
        return date.toLocaleString(countryCode, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    };

    const formatDateTwoLines = (timestamp: number, overrideCountryCode?: string) => {
        const d = new Date(timestamp * 1000);
        return {
            date: d.toLocaleDateString(overrideCountryCode || countryCode, {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
            }),
            time: d.toLocaleTimeString(overrideCountryCode || countryCode, {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            }),
        };
    };
// Advanced smart formatters
const formatSmartNumber = (value: number): string => {
    return formatWithAbbreviation(value, countryCode);
};

const formatSmartQuantity = (value: number): string => {
    return formatQuantity(value, countryCode);
};

const formatSmartPrice = (value: number): string => {
    return formatPrice(value, currency, countryCode);
};

const formatSmartCurrency = (value: number): string => {
    return formatPrice(value, currency, countryCode);
};

// Format with tooltip data for displaying full precision in tooltip
const formatWithTooltipWrapper = (value: number) => {
    return formatWithTooltip(value, countryCode);
};


    const formatDateShort = (date: Date | number, overrideCountryCode?: string) => {
        const timestamp = date instanceof Date ? date.getTime() / 1000 : date;
        const d = new Date(timestamp * 1000);
        return d.toLocaleDateString(overrideCountryCode || countryCode, {
            day: 'numeric',
            month: 'short'
        });
    };

    const formatDateLong = (date: Date | number, overrideCountryCode?: string) => {
        const timestamp = date instanceof Date ? date.getTime() / 1000 : date;
        const d = new Date(timestamp * 1000);
        return d.toLocaleDateString(overrideCountryCode || countryCode, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatMonthYear = (date: Date | number, overrideCountryCode?: string) => {
        const timestamp = date instanceof Date ? date.getTime() / 1000 : date;
        const d = new Date(timestamp * 1000);
        return d.toLocaleDateString(overrideCountryCode || countryCode, {
            month: 'short',
            year: 'numeric'
        });
    };

    // Custom formatter for transaction dates with separate date and time parts
    const formatTransactionDate = (timestamp: number, overrideCountryCode?: string): { date: string; time: string } => {
        const date = new Date(timestamp * 1000);
        const day = date.getDate();
        const month = date.toLocaleString(overrideCountryCode || countryCode, { month: 'short' });
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        
        return {
            date: `${day} ${month} ${year}`,
            time: `${hours}:${minutes}:${seconds}`
        };
    };

    const value = {
        formatNumber,
        formatCrypto,
        formatDate,
        formatDateTwoLines,
        formatCurrency,
        formatPercentage,
        // Advanced formatters
        formatSmartNumber,
        formatSmartQuantity,
        formatSmartPrice,
        formatSmartCurrency,
        formatWithTooltip: formatWithTooltipWrapper,
        formatDateShort,
        formatDateLong,
        formatMonthYear,
        formatTransactionDate,
    };

    return <FormatterContext.Provider value={value}>{children}</FormatterContext.Provider>;
};

export default FormatterContext;
